
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DiagnosticResult } from './types';
import { toast } from 'sonner';

export const useDiagnostics = () => {
  const [results, setResults] = useState<DiagnosticResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [fixingAdmin, setFixingAdmin] = useState(false);
  const { refreshAssignedBases, checkIsAdmin } = useAuth();

  const runDiagnostics = async () => {
    try {
      setLoading(true);
      // Use raw query to bypass TypeScript limitations
      const { data, error } = await supabase.rpc('run_admin_diagnostics' as any);
      
      if (error) {
        console.error("Error running diagnostics:", error);
        toast.error("Failed to run diagnostics: " + error.message);
        return;
      }
      
      console.log("Diagnostic results:", data);
      setResults(data as DiagnosticResult);
      toast.success("Diagnostics completed successfully");
    } catch (err: any) {
      console.error("Exception in runDiagnostics:", err);
      toast.error("An error occurred: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fixAdminAccess = async () => {
    try {
      setFixingAdmin(true);
      // Use raw query to bypass TypeScript limitations
      const { data, error } = await supabase.rpc('ensure_admin_access' as any);
      
      if (error) {
        console.error("Error fixing admin access:", error);
        toast.error("Failed to fix admin access: " + error.message);
        return;
      }
      
      console.log("Admin fix results:", data);
      toast.success("Admin access has been fixed");
      
      // Refresh auth context
      await checkIsAdmin();
      await refreshAssignedBases();
      
      // Re-run diagnostics
      runDiagnostics();
    } catch (err: any) {
      console.error("Exception in fixAdminAccess:", err);
      toast.error("An error occurred: " + err.message);
    } finally {
      setFixingAdmin(false);
    }
  };

  return {
    results,
    loading,
    fixingAdmin,
    runDiagnostics,
    fixAdminAccess
  };
};
